const STORAGE_KEY = 'adnuntius.network'
const EXTRA_STORAGE_KEY = 'adnuntius.network.extra'
const USER_NETWORK_STORAGE_KEY = 'adnuntius.network.byUser'

/**
 * The primary network. Everything the app does outside of the advertising list
 * queries — single-object GETs, saves, currency and tier defaults, publishing,
 * reports — is scoped to this one network.
 */
export function getContext(): string {
  return localStorage.getItem(STORAGE_KEY) || ''
}

/**
 * The last primary network each user worked in on this browser, keyed by user
 * id. `adnuntius.network` is shared by whoever signs in next, so a different
 * account (or a masquerade) overwrites it; this map is what brings a user back
 * to their own last network when they sign in again.
 */
function readUserNetworks(): Record<string, string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(USER_NETWORK_STORAGE_KEY) ?? 'null')
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

export function getUserNetwork(userId: string): string | null {
  const tagId = readUserNetworks()[userId]
  return typeof tagId === 'string' && tagId ? tagId : null
}

export function setUserNetwork(userId: string, tagId: string) {
  try {
    const map = readUserNetworks()
    if (map[userId] === tagId) return
    localStorage.setItem(USER_NETWORK_STORAGE_KEY, JSON.stringify({ ...map, [userId]: tagId }))
  } catch {
    // ignore — the shared key still remembers the network for this browser
  }
}

/**
 * Extra networks whose objects the advertising *list* endpoints should also
 * return, on top of the primary context. Sent as the semicolon-separated
 * `network` query parameter, which the API declares only on the advertiser
 * chain's query() methods: advertisers, orders, line items and creatives.
 *
 * Nothing else honours it — a single-object GET, a save or any other endpoint
 * still resolves against the primary context alone.
 */
export const MAX_EXTRA_NETWORKS = 20

export function getExtraNetworks(): string[] {
  try {
    const raw = localStorage.getItem(EXTRA_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((v): v is string => typeof v === 'string' && !!v)
  } catch {
    return []
  }
}

export function setExtraNetworks(tagIds: string[]) {
  try {
    localStorage.setItem(EXTRA_STORAGE_KEY, JSON.stringify(tagIds.slice(0, MAX_EXTRA_NETWORKS)))
  } catch {
    // ignore — a full or unavailable localStorage just means extras don't persist
  }
}

/**
 * `context` plus, when extra networks are selected, `network`. Spread into the
 * URLSearchParams of an advertising list query; on its own it is just the
 * primary context, so it is safe to use unconditionally on those four lists.
 */
export function listScopeParams(): Record<string, string> {
  const context = getContext()
  const extras = getExtraNetworks().filter((tagId) => tagId !== context)
  return extras.length > 0
    ? { context, network: extras.slice(0, MAX_EXTRA_NETWORKS).join(';') }
    : { context }
}
